/**
 * apps/katalog-app/src/pages/components/AuthDrawer.tsx
 *
 * Auth drawer that renders AuthForm in:
 * - Right-side drawer on desktop (always visible)
 * - Right-side drawer on mobile (using RightDrawerShell)
 *
 * Listens for auth state changes to trigger onAuthSuccess + resume pending action.
 */
import React, { useEffect, useRef } from 'react';
import { useAuth } from '@bright-web-skeleton/auth';
import { RightDrawerShell } from '@bright-web-skeleton/ui/components/layout/RightDrawerShell';
import { useAuthDrawer } from '../../hooks/useAuthDrawer';
import { AuthForm } from './AuthForm';
import { X } from 'lucide-react';

export const AuthDrawer: React.FC = () => {
  const { open, closeDrawer, onAuthSuccess } = useAuthDrawer();
  const { authUser } = useAuth();
  const prevAuthUserRef = useRef(authUser);

  // Watch for auth state transition: null → user (login/signup completed)
  useEffect(() => {
    const wasLoggedOut = !prevAuthUserRef.current;
    const isNowLoggedIn = !!authUser;
    prevAuthUserRef.current = authUser;

    if (wasLoggedOut && isNowLoggedIn && open) {
      // Auth just completed while drawer was open → bind invite + resume action
      onAuthSuccess(authUser.id);
    }
  }, [authUser, open, onAuthSuccess]);

  const drawerHeader = (
    <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-border bg-card">
      <span className="text-sm font-semibold text-foreground">Sign in to continue</span>
      <button
        onClick={closeDrawer}
        className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
        aria-label="Close auth panel"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );

  const formContent = (
    <div className="p-4">
      <AuthForm compact onSuccess={(userId) => onAuthSuccess(userId)} />
    </div>
  );

  return (
    <>
      {/* Mobile: RightDrawerShell */}
      <RightDrawerShell
        open={open}
        variant="custom"
        onClose={closeDrawer}
        header={drawerHeader}
        widthClassName="w-[90vw] max-w-[400px]"
        topOffset={0}
      >
        {formContent}
      </RightDrawerShell>

      {/* Desktop: slide-over panel */}
      {open && (
        <div className="hidden md:block fixed inset-0 z-50">
          {/* Scrim */}
          <div className="absolute inset-0 bg-black/40" onClick={closeDrawer} />

          {/* Panel */}
          <aside className="absolute right-0 top-0 h-full w-full max-w-[420px] bg-card border-l border-border shadow-xl flex flex-col animate-in slide-in-from-right duration-200">
            {drawerHeader}
            <div className="flex-1 overflow-y-auto p-6">
              <AuthForm compact onSuccess={(userId) => onAuthSuccess(userId)} />
            </div>
          </aside>
        </div>
      )}
    </>
  );
};
